import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { GraduationCap, PlayCircle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyTrainingPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/employee-portal/training/courses`);
        setCourses(r.data.courses || r.data || []);
      } catch(e){ if(e?.response?.status !== 403) toast.error('Failed to load training'); }
      finally { setLoading(false); }
    })();
  }, []);

  const completed = courses.filter(c => c.progress >= 100 || c.completed).length;
  const inProgress = courses.filter(c => c.progress > 0 && c.progress < 100 && !c.completed).length;

  return (
    <div className="min-h-screen p-6 bg-gray-50" data-testid="employee-training-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Training & Development</h1>
          <p className="text-gray-600 mt-1">Courses assigned to you.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-gray-500">Assigned</div><div className="text-2xl font-bold">{courses.length}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-gray-500">In progress</div><div className="text-2xl font-bold text-blue-600">{inProgress}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-gray-500">Completed</div><div className="text-2xl font-bold text-emerald-600">{completed}</div></CardContent></Card>
        </div>

        {loading ? <div className="text-gray-500">Loading…</div> :
         courses.length === 0 ? <Card><CardContent className="p-8 text-center text-gray-500 italic">No training assigned yet.</CardContent></Card> :
         <div className="grid md:grid-cols-2 gap-4">
           {courses.map((c, i) => {
             const pct = c.progress || 0;
             const done = pct >= 100 || c.completed;
             return (
               <Card key={i} data-testid={`training-card-${i}`}>
                 <CardHeader className="flex flex-row items-start justify-between pb-3">
                   <div className="flex items-center gap-3">
                     <div className="bg-indigo-100 p-2 rounded-lg"><GraduationCap className="h-5 w-5 text-indigo-600" /></div>
                     <div>
                       <CardTitle className="text-base leading-tight">{c.title || c.name}</CardTitle>
                       {c.duration_hours && <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Clock className="h-3 w-3" />{c.duration_hours}h</div>}
                     </div>
                   </div>
                   {done
                     ? <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" />Done</Badge>
                     : <Badge variant="outline" className="text-blue-700 border-blue-300"><PlayCircle className="h-3 w-3 mr-1" />In progress</Badge>}
                 </CardHeader>
                 <CardContent className="space-y-3">
                   <Progress value={pct} className="h-2" />
                   <div className="text-xs text-gray-500">{pct}% complete{c.due_date ? ` · due ${c.due_date}` : ''}</div>
                 </CardContent>
               </Card>
             );
           })}
         </div>}
      </div>
    </div>
  );
}
